import { styled } from 'styled-components';
import {
  apcaContrast,
  blendOver,
  hexToRGB,
} from '../../../utils/helpers/colorDifference';

const getColorWithOpacity = (hexColour, opacity) => {
  const hexOpacity = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hexColour}${hexOpacity}`;
};

// The bar the reader sees is the position colour laid over the cell background
// at the row's opacity (blendOver), so decide the ink from that blend.
//
// Give the bar whichever of the theme's two inks reads better on the blend by
// APCA: the normal ink, or the tile background used as an inverse ink. We use
// canvas.tertiary rather than ink.inverse deliberately — on the plain tile its
// contrast is nil, so faint bars keep the normal ink, while bright and pale
// bars flip to the tile colour. Because APCA weighs each polarity correctly,
// one rule covers both modes with no tuned threshold. The stronger |Lc| wins;
// ties keep the normal ink.
export const getBarTextColour = (theme, colour, opacity) => {
  const fill = blendOver(theme.colours.canvas.tertiary.main, colour, opacity);
  const inkPrimary = theme.colours.ink.primary.main;
  const tileBackground = theme.colours.canvas.tertiary.main;
  return Math.abs(apcaContrast(hexToRGB(inkPrimary), fill)) >=
    Math.abs(apcaContrast(hexToRGB(tileBackground), fill))
    ? inkPrimary
    : tileBackground;
};

export const DepthChartTableContainer = styled.div`
  border: ${({ theme }) => `1px solid ${theme.colours.canvas.secondary.main}`};
  height: 100%;
  display: flex;
  flex-direction: column;

  a {
    text-decoration: underline;
  }

  ${({ $selectableRows }) =>
    $selectableRows
      ? `
        th:first-of-type {
          width: 2rem;
        }

        th:nth-of-type(n+3) {
          width: 3.75rem;
        }
      `
      : `
        th:not(:first-of-type) {
          width: 3.75rem;
        }
      `}

  td {
    position: relative;
  }

  tbody tr:nth-of-type(odd) td {
    background-color: ${({ theme }) => theme.colours.canvas.tertiary.main};
  }

  tbody tr td:first-of-type {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 6.25rem;
  }

  // Fill the space the header leaves, whatever the typeface's line height,
  // rather than subtracting a fixed header height. min-height: 0 lets the
  // region shrink so its own scrollbar appears only when rows truly overflow.
  > div.scrollable {
    flex: 1;
    min-height: 0;
    overflow-y: auto;

    ::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.colours.layering[1].strong};
    }

    ::-webkit-scrollbar-corner,
    ::-webkit-slider-track {
      background: transparent;
    }
  }
`;

export const StyledTableDataCell = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  padding-top: ${({ theme }) => theme.sizing.tiny};
  padding-right: ${({ theme }) => theme.sizing.small};
  background-color: ${({ $color, $opacity }) =>
    getColorWithOpacity($color, $opacity)};
  color: ${({ theme, $color, $opacity }) =>
    getBarTextColour(theme, $color, $opacity)};
`;

export const DepthChartTableHeader = styled.header`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.colours.canvas.secondary.main};
  padding: ${({ theme }) => theme.sizing.small};

  h3 {
    font-family: ${({ theme }) => theme.font.family};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.sizing.small};
    ${({ theme }) => theme.typography.headingSmall}
    color: ${({ theme }) => theme.colours.ink.primary.main};
    margin: 0;
  }
`;

DepthChartTableContainer.displayName = 'DepthChartTableContainer';
StyledTableDataCell.displayName = 'StyledTableDataCell';
DepthChartTableHeader.displayName = 'DepthChartTableHeader';
